import * as React from "react"
import { graphql } from "gatsby"

import MainLayout from "@/layouts/MainLayout"
import FeaturedBlogPostPreview from "@/components/FeaturedBlogPostPreview"
import { Link, useTranslation } from "@zestia/gatsby-plugin-react-i18next"

import SharedHead from "@/components/SharedHead"
import useHeadTranslation from "@/hooks/use-head-translation"
import BlogPostPreviewRow from "@/components/BlogPostPreviewRow"
import NewsletterSignup from "@/components/NewsletterSignup"
import blogCategories from "@/data/blog-categories"
import transformBlogPosts from "@/utils/transformBlogPosts"
import SanityGatsbyImage from "@/components/SanityGatsbyImage"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import FallbackImage from "@/images/blog/generic-image.png"
import { gtmTracking } from "@/utils/gtm-tracking"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  const title = t("blog.home.title.all")
  const description = t("blog.home.description.all")

  return <SharedHead {...props} title={title} description={description} />
}

const Blog = ({ data }) => {
  const { t } = useTranslation()

  // Combine all the blog posts from Sanity and MDX
  const allBlogPosts = transformBlogPosts({
    allMdx: data.allMdx,
    allSanityBlogPost: data.allSanityBlogPost,
  })

  const bigPost = allBlogPosts[0]

  const featuredPosts = allBlogPosts
    .filter(post => post.frontmatter.featured === true && post !== bigPost)
    .slice(0, 5)

  // If there are less than 5 featured posts, supplement the remaining amount with the most recent posts
  if (featuredPosts.length < 5) {
    const remainingPosts = data.allMdx.nodes
      .filter(
        post =>
          post.frontmatter.featured !== true &&
          post.frontmatter.slug !== bigPost.frontmatter.slug,
      )
      .slice(0, 5 - featuredPosts.length)
    featuredPosts.push(...remainingPosts)
  }

  // Loop through each category in both MDX and sanity, and combine them
  const postsByCategory = blogCategories.map(category => {
    // Find the category in the MDX and Sanity data
    const mdxCategory = data.allMdx.postsByCategory.find(
      cat => cat.fieldValue === category,
    )
    const sanityCategory = data.allSanityBlogPost.postsByCategory.find(
      cat => cat.fieldValue === category,
    )

    // Combine the posts from both sources
    const posts = transformBlogPosts({
      allMdx: {
        nodes: mdxCategory ? mdxCategory.nodes : [],
      },
      allSanityBlogPost: {
        nodes: sanityCategory ? sanityCategory.nodes : [],
      },
    })
      .sort(
        (a, b) => new Date(b.frontmatter.date) - new Date(a.frontmatter.date),
      )
      .slice(0, 4)

    return {
      fieldValue: category,
      nodes: posts,
    }
  })

  return (
    <MainLayout
      trackingData={{
        content_category: "blog",
      }}
    >
      <div className="container-fluid mt-10 md:mt-20 blog">
        {/* Latest Post & 5 featured posts */}
        <section className="blog__top">
          {/* Most recent post */}
          {bigPost && <FeaturedBlogPostPreview post={bigPost} />}

          {featuredPosts && (
            <div className="blog__big-posts md:pl-16 lg:pl-32 mt-8 md:mt-0">
              {featuredPosts.map(post => (
                <Link
                  to={post.frontmatter.slug}
                  className="blog__featured-post"
                  key={post.frontmatter.slug}
                  onClick={() =>
                    gtmTracking(
                      "Link Click",
                      post.frontmatter.title,
                      `Featured Blog Post | Category: ${
                        post.frontmatter.categories[0].charAt(0).toUpperCase() +
                        post.frontmatter.categories[0].slice(1)
                      }`,
                    )()
                  }
                >
                  <div className="blog__featured-post__image">
                    {post.frontmatter.preview_image ? (
                      post.internal.type === "SanityBlogPost" ? (
                        <SanityGatsbyImage
                          image={post.frontmatter.preview_image}
                          maxWidth={200}
                          alt={post.frontmatter.title}
                          style={{
                            width: "100%",
                            height: "100%",
                          }}
                          objectFit="cover"
                        />
                      ) : (
                        <GatsbyImage
                          image={getImage(post.frontmatter.preview_image.src)}
                          alt={post.frontmatter.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )
                    ) : (
                      <img
                        src={FallbackImage}
                        alt={post.frontmatter.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </div>

                  <div className="blog__featured-post__content">
                    <h3 className="mb-0 h6">{post.frontmatter.title}</h3>

                    <p className="mb-0 p--sm">
                      {t(`blog.category.${post.frontmatter.categories[0]}`)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Posts by category */}
        <section className="blog__categories mt-10 md:mt-20">
          {postsByCategory &&
            postsByCategory.map(category => (
              <BlogPostPreviewRow
                key={t(`blog.category.${category.fieldValue}`)}
                title={t(`blog.category.${category.fieldValue}`)}
                posts={category.nodes}
                link={`/blog/category/${category.fieldValue}`}
              />
            ))}
        </section>

        {/* Newsletter Signup Form */}
        <NewsletterSignup className="mt-20 mb-20 newsletter-signup--no-border-bottom" />
      </div>
    </MainLayout>
  )
}

export const query = graphql`
  query ($language: String!, $categories: [String!]) {
    ...Locales

    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "blog/generic-image.png" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData(width: 400, placeholder: BLURRED)
          }
        }
      }
    }

    allSanityBlogPost(
      sort: { publishedAt: DESC }
      filter: {
        categories: { in: $categories }
        language: { eq: $language }
        hidden: { ne: true }
        hiddenFromPageOnly: { ne: true }
      }
    ) {
      nodes {
        slug {
          current
        }
        title
        author {
          name
        }
        categories
        summary
        publishedAt
        featured
        coverImage {
          ...SanityImageData
        }
        internal {
          type
        }
      }

      # First 4 posts in each category
      postsByCategory: group(field: { categories: SELECT }, limit: 4) {
        fieldValue
        nodes {
          slug {
            current
          }
          title
          author {
            name
          }
          categories
          summary
          publishedAt
          featured
          coverImage {
            ...SanityImageData
          }
          internal {
            type
          }
        }
      }
    }

    allMdx(
      sort: { frontmatter: { date: DESC } }
      filter: {
        fields: {
          lang: { eq: $language }
          path: { regex: "/(mdxPages/blog)/" }
        }
        frontmatter: { hidden: { ne: true }, categories: { in: $categories } }
      }
    ) {
      nodes {
        id
        fields {
          lang
          timeToRead
        }
        body
        frontmatter {
          date
          author
          slug
          summary
          title
          categories
          featured
          social_image {
            alt
            src {
              childImageSharp {
                gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP])
              }
            }
          }
          preview_image {
            alt
            src {
              childImageSharp {
                gatsbyImageData(width: 400, placeholder: BLURRED)
              }
            }
          }
        }
        internal {
          type
        }
      }

      # First 4 posts in each category
      postsByCategory: group(
        field: { frontmatter: { categories: SELECT } }
        limit: 4
      ) {
        fieldValue
        nodes {
          id
          fields {
            lang
            timeToRead
          }
          body
          frontmatter {
            hidden
            author
            slug
            summary
            title
            categories
            date
            preview_image {
              alt
              src {
                childImageSharp {
                  gatsbyImageData(width: 400, placeholder: BLURRED)
                }
              }
            }
          }
          internal {
            type
          }
        }
      }
    }
  }
`

export default Blog
